import { BackdropLoading } from '@/components/backdrop-loading'
import { ModalActions, ModalContainer } from '@/components/container/container'
import { FormGroup } from '@/components/form/form-group'
import {
    createAgenda,
    createAgendaEvent,
    createRecurringEventConfig,
    getAgendasByProfessional,
    RecurringFrequency,
} from '@/lib/api/agenda/agenda-api'
import { Client, getClientsByOrganization } from '@/lib/api/clients/client-api'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useAuth } from '@/store/hooks'
import { formatISO } from 'date-fns'
import { format, toDate } from 'date-fns-tz'
import React, { useEffect, useState } from 'react'
import { MdClose, MdEvent, MdPerson } from 'react-icons/md'
import { toast } from 'react-toastify'
import type { AgendaMode } from './agenda'
import { isTaskType, TASK_DEFAULT_DURATION_MINUTES, TASK_TYPE_LABELS } from './utils/status-colors'
import type { AgendaEventType } from '@/lib/api/common/interfaces/agenda.interface'
import { RoomSelector } from './components/rooms/RoomSelector'

const timeZone = 'America/Sao_Paulo'

const FREQUENCY_LABELS: Record<RecurringFrequency, string> = {
    DAILY: 'Diario',
    WEEKLY: 'Semanal',
    BIWEEKLY: 'Quinzenal',
    MONTHLY: 'Mensal',
    QUARTERLY: 'Trimestral',
    SEMIANNUAL: 'Semestral',
    ANNUAL: 'Anual',
}

function calculateNextOccurrence(
    baseDate: Date,
    freq: RecurringFrequency,
): Date {
    const next = new Date(baseDate)
    const originalDay = baseDate.getDate()
    switch (freq) {
        case 'DAILY':
            next.setDate(next.getDate() + 1)
            break
        case 'WEEKLY':
            next.setDate(next.getDate() + 7)
            break
        case 'BIWEEKLY':
            next.setDate(next.getDate() + 14)
            break
        case 'MONTHLY':
            next.setMonth(next.getMonth() + 1)
            if (next.getDate() !== originalDay) next.setDate(0)
            break
        case 'QUARTERLY':
            next.setMonth(next.getMonth() + 3)
            if (next.getDate() !== originalDay) next.setDate(0)
            break
        case 'SEMIANNUAL':
            next.setMonth(next.getMonth() + 6)
            if (next.getDate() !== originalDay) next.setDate(0)
            break
        case 'ANNUAL':
            next.setFullYear(next.getFullYear() + 1)
            if (next.getDate() !== originalDay) next.setDate(0)
            break
    }
    return next
}

interface Props {
    onClose: () => void
    onSuccess: () => void
    professionalId?: string
    date?: Date
    agendaMode?: AgendaMode
    agendaId?: number
}

export function AddEventModal({
    onClose,
    onSuccess,
    professionalId,
    date,
    agendaMode = 'professional',
    agendaId,
}: Props) {
    const { user, isPartner, isEmployee } = useAuth()
    const { clientLabel } = useClientLabel()
    const [loading, setLoading] = useState(false)
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [dateStr, setDateStr] = useState('')
    const [hour, setHour] = useState('')
    const [endDate, setEndDate] = useState('')
    const [clientId, setClientId] = useState<number | null>(null)
    // Agenda pessoal e da empresa: compromisso comum ("Outro") ou tarefa de vendas.
    const [eventType, setEventType] = useState<string>('OTHER')
    const [clientSearch, setClientSearch] = useState('')
    const [clients, setClients] = useState<Client[]>([])
    const [showClientSearch, setShowClientSearch] = useState(false)
    const [selectedClient, setSelectedClient] = useState<Client | null>(null)
    const [searchingClients, setSearchingClients] = useState(false)
    const [roomId, setRoomId] = useState<number | null>(null)
    const hideClientSelector = agendaMode !== 'professional'
    // Parceiro não é mais escolhido na agenda — só herda o do próprio usuário parceiro,
    // quando houver. O agendamento de consultas/exames/procedimentos (com parceiro) é
    // feito nas telas dedicadas de cada tipo.
    const partnerId =
        isPartner || (isEmployee && !!user?.partnerId)
            ? user?.partnerId || null
            : null
    const [isRecurring, setIsRecurring] = useState(false)
    const [frequency, setFrequency] = useState<RecurringFrequency>('WEEKLY')

    const isProfessionalMode = agendaMode === 'professional'

    const getDefaultNotificationEmail = (): string => {
        if (agendaMode === 'personal') {
            return user?.email || ''
        }
        if (agendaMode === 'company') {
            if (user?.partnerId && user?.partner) {
                return user.partner.email || ''
            }
            return user?.company?.email || ''
        }
        // professional: vazio ate selecionar cliente
        return ''
    }

    const getDefaultWhatsapp = (): string => {
        if (agendaMode === 'personal') {
            return user?.phone || ''
        }
        if (agendaMode === 'company') {
            if (user?.partnerId && user?.partner) {
                return user.partner.whatsappNumber || ''
            }
            return user?.company?.whatsappNumber || ''
        }
        // professional: vazio ate selecionar cliente
        return ''
    }

    const [notificationEmail, setNotificationEmail] = useState(
        getDefaultNotificationEmail()
    )
    const [whatsappNumber, setWhatsappNumber] = useState(
        getDefaultWhatsapp()
    )

    useEffect(() => {
        if (date) {
            setDateStr(format(date, 'dd/MM/yyyy', { timeZone }))
            setHour(format(date, 'HH:mm', { timeZone }))
        }
    }, [date])

    useEffect(() => {
        if (clientSearch.trim() && user?.companyId) {
            const timer = setTimeout(() => {
                searchClient()
            }, 300)

            return () => clearTimeout(timer)
        } else {
            setClients([])
        }
    }, [clientSearch, user?.companyId])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement
            if (
                !target.closest('.client-search-container') &&
                showClientSearch
            ) {
                setShowClientSearch(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showClientSearch])

    const formatTime = (value: string): string => {
        const numbers = value.replace(/\D/g, '')
        const limited = numbers.slice(0, 4)
        if (limited.length <= 2) {
            return limited
        }
        return `${limited.slice(0, 2)}:${limited.slice(2, 4)}`
    }

    const searchClient = async () => {
        if (!user?.companyId || !clientSearch.trim()) return

        setSearchingClients(true)
        try {
            const response = await getClientsByOrganization(user.organizationId, {
                page: 1,
                limit: 50,
                search: clientSearch,
            })
            if (response.success) {
                setClients(response.result)
            }
        } catch (error) {
            console.error('Erro ao buscar clientes:', error)
        } finally {
            setSearchingClients(false)
        }
    }

    // Tarefa não ocupa horário nem avisa por e-mail/WhatsApp (docs/sales-tasks.md).
    const isTask = !isProfessionalMode && isTaskType(eventType)

    const handleSubmit = async () => {
        if (!title.trim()) {
            return toast.error('Escreva o titulo do agendamento')
        }

        if (isProfessionalMode && !clientId) {
            return toast.error(`Selecione um ${clientLabel}`)
        }

        if (isProfessionalMode && !professionalId) {
            return toast.error('Selecione um profissional')
        }

        if (!dateStr) {
            return toast.error('Defina a data')
        }

        if (!hour) {
            return toast.error('Defina a hora')
        }

        const [hours, minutes] = hour.split(':').map(Number)

        // Tarefa sem término usa a duração padrão, limitada ao fim do dia.
        let endTime = endDate
        if (!endTime && isTask) {
            const endMinutes = Math.min(hours * 60 + minutes + TASK_DEFAULT_DURATION_MINUTES, 23 * 60 + 59)
            endTime = `${String(Math.floor(endMinutes / 60)).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`
        }

        if (!endTime) {
            return toast.error('Defina a previsão de término')
        }

        const [hours2, minutes2] = endTime.split(':').map(Number)

        if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
            return toast.error('Horas invalidas!')
        }

        if (hours2 < 0 || hours2 > 23 || minutes2 < 0 || minutes2 > 59) {
            return toast.error('Horas de término inválidas!')
        }

        const split = dateStr.split('/')

        if (split.length !== 3) {
            return toast.error('Data invalida! Use o formato DD/MM/AAAA')
        }

        const strDate = `${split[2]}-${split[1]}-${split[0]} ${hour}:00`
        const strEndDate = `${split[2]}-${split[1]}-${split[0]} ${endTime}:00`

        const parsedDate = toDate(strDate, {
            timeZone,
        })

        const parsedEndDate = toDate(strEndDate, {
            timeZone,
        })

        const hourEndDate = parsedEndDate.getHours()
        const hourDate = parsedDate.getHours()
        const minuteEndDate = parsedEndDate.getMinutes()
        const minuteDate = parsedDate.getMinutes()

        if (
            (hourEndDate === 0 ? 24 : hourEndDate) <
            (hourDate === 0 ? 24 : hourDate) ||
            (hourEndDate === hourDate && minuteEndDate <= minuteDate)
        ) {
            return toast.error(
                'A hora da previsão de término precisa ser maior que o início!'
            )
        }

        const isoDate = formatISO(parsedDate)
        const isoEndDate = formatISO(parsedEndDate)

        setLoading(true)
        try {
            let finalAgendaId: number

            if (isProfessionalMode) {
                const agendasResponse = await getAgendasByProfessional(
                    parseInt(professionalId!)
                )

                if (!agendasResponse.success || !agendasResponse.result) {
                    const createAgendaResponse = await createAgenda({
                        professionalId: parseInt(professionalId!),
                    })

                    if (
                        !createAgendaResponse.success ||
                        !createAgendaResponse.result
                    ) {
                        toast.error(
                            createAgendaResponse.error ||
                            'Erro ao criar agenda para o profissional'
                        )
                        return
                    }

                    finalAgendaId = createAgendaResponse.result.id
                } else {
                    finalAgendaId = agendasResponse.result.id
                }
            } else {
                if (!agendaId) {
                    toast.error('Agenda nao encontrada')
                    return
                }
                finalAgendaId = agendaId
            }

            // A agenda só cria eventos genéricos ("Outro"). Consultas, exames, retornos e
            // procedimentos são agendados nas telas dedicadas, então nunca criamos a
            // entidade correspondente a partir daqui.
            const response = await createAgendaEvent({
                title,
                description: description || undefined,
                agendaId: finalAgendaId,
                clientId: clientId || undefined,
                roomId: isTask ? undefined : roomId || undefined,
                partnerId: partnerId || undefined,
                type: isProfessionalMode ? 'OTHER' : (eventType as AgendaEventType),
                startDate: isoDate,
                endDate: isoEndDate,
                status: 'PENDING',
                notificationEmail: isTask ? undefined : notificationEmail.trim() || undefined,
                whatsappNotificationNumber: isTask ? undefined : whatsappNumber.trim() || undefined,
                createEntity: false,
                professionalId: isProfessionalMode
                    ? parseInt(professionalId!)
                    : undefined,
                companyId: user?.companyId || undefined,
            })

            if (response.success) {
                if (isRecurring && !isProfessionalMode && !isTask) {
                    const nextOccurrence = calculateNextOccurrence(
                        parsedDate,
                        frequency,
                    )

                    await createRecurringEventConfig({
                        agendaId: finalAgendaId,
                        title,
                        description: description || undefined,
                        type: eventType as AgendaEventType,
                        frequency,
                        startTime: hour,
                        endTime,
                        notificationEmail:
                            notificationEmail.trim() || undefined,
                        whatsappNotificationNumber:
                            whatsappNumber.trim() || undefined,
                        clientId: clientId || undefined,
                        roomId: roomId || undefined,
                        partnerId: partnerId || undefined,
                        companyId: user?.companyId || undefined,
                        userId:
                            agendaMode === 'personal'
                                ? user?.id
                                : undefined,
                        nextOccurrence: formatISO(nextOccurrence),
                    }).catch(() => {
                        toast.error(
                            'Evento criado, mas erro ao configurar recorrencia',
                        )
                    })
                }

                toast.success('Evento criado com sucesso')
                onSuccess()
                onClose()
            } else {
                toast.error(response.error || 'Erro ao criar evento')
            }
        } catch (error: any) {
            toast.error(error.message || 'Erro ao criar evento')
        } finally {
            setLoading(false)
        }
    }

    if (isProfessionalMode && !professionalId) {
        return (
            <React.Fragment>
                <dialog className="modal modal-open">
                    <ModalContainer size="sm" className="!overflow-visible">
                        <div>Selecione um profissional</div>
                        <ModalActions>
                            <button className="btn btn-ghost" onClick={onClose}>
                                Fechar
                            </button>
                        </ModalActions>
                    </ModalContainer>
                </dialog>
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <dialog className="modal modal-open">
                <ModalContainer size="sm" className="!overflow-visible">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-secondary/10">
                            <MdEvent className="text-xl text-secondary" />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg">Novo Evento</h3>
                            <p className="text-sm text-base-content/60">
                                {isProfessionalMode
                                    ? 'Adicionar agendamento'
                                    : agendaMode === 'personal'
                                        ? 'Adicionar evento pessoal'
                                        : 'Adicionar evento da empresa'}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <FormGroup>
                            <input
                                type="text"
                                placeholder={isProfessionalMode ? 'Título, ex.: Consulta' : 'Título, ex.: Ligar para confirmar'}
                                className="input input-bordered w-full"
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                required
                            />
                        </FormGroup>

                        {!isProfessionalMode && (
                            <div className="form-control">
                                <label className="label" htmlFor="agenda-event-type">
                                    <span className="label-text text-sm">Tipo</span>
                                </label>
                                <select
                                    id="agenda-event-type"
                                    className="select select-bordered w-full"
                                    value={eventType}
                                    onChange={e => setEventType(e.target.value)}
                                >
                                    <option value="OTHER">Compromisso</option>
                                    <optgroup label="Tarefa">
                                        {Object.entries(TASK_TYPE_LABELS).map(([value, label]) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                    </optgroup>
                                </select>
                                {isTaskType(eventType) && (
                                    <p className="mt-1 text-xs leading-relaxed text-base-content/60">
                                        Tarefa não bloqueia o horário e lembra só no sino. Para ligar a um lead, crie pela tela do lead.
                                    </p>
                                )}
                            </div>
                        )}

                        {!isTask && (
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text text-sm">
                                        Sala (opcional)
                                    </span>
                                </label>
                                <RoomSelector
                                    value={roomId}
                                    onChange={setRoomId}
                                    placeholder="Selecione"
                                />
                            </div>
                        )}

                        <FormGroup flexGrowItems={[0, 0, 1]} flexibleSize>
                            <input
                                type="text"
                                placeholder="Data (DD/MM/AAAA)"
                                className="input input-bordered w-full"
                                data-mask="99/99/9999"
                                value={dateStr}
                                onChange={e => setDateStr(e.target.value)}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Hora (00:00)"
                                className="input input-bordered w-full"
                                data-mask="99:99"
                                value={hour}
                                onChange={e =>
                                    setHour(formatTime(e.target.value))
                                }
                                maxLength={5}
                                required
                            />
                            <input
                                type="text"
                                placeholder={isTask ? 'Término (opcional)' : 'Término (00:00)'}
                                className="input input-bordered w-full"
                                data-mask="99:99"
                                value={endDate}
                                onChange={e =>
                                    setEndDate(formatTime(e.target.value))
                                }
                                maxLength={5}
                                required={!isTask}
                            />
                        </FormGroup>

                        <FormGroup>
                            <div className="relative client-search-container">
                                {selectedClient ? (
                                    <div className="flex items-center justify-between p-3 border border-base-300 rounded-lg bg-base-100">
                                        <div className="flex items-center gap-2">
                                            <MdPerson className="text-base-content/60" />
                                            <span className="font-medium">
                                                {selectedClient.name}
                                            </span>
                                        </div>
                                        <button
                                            className="btn btn-xs btn-ghost"
                                            onClick={() => {
                                                setSelectedClient(null)
                                                setClientId(null)
                                                setClientSearch('')
                                                if (isProfessionalMode) {
                                                    setNotificationEmail('')
                                                    setWhatsappNumber('')
                                                }
                                            }}
                                        >
                                            <MdClose />
                                        </button>
                                    </div>
                                ) : !hideClientSelector && (
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder={`Buscar ${clientLabel}${!isProfessionalMode ? ' (opcional)' : ''}`}
                                            className="input input-bordered w-full"
                                            value={clientSearch}
                                            onChange={e => {
                                                setClientSearch(e.target.value)
                                                setShowClientSearch(true)
                                            }}
                                            onFocus={() =>
                                                setShowClientSearch(true)
                                            }
                                            required={isProfessionalMode}
                                        />
                                        {showClientSearch &&
                                            (clients.length > 0 ||
                                                searchingClients) && (
                                                <div className="absolute z-50 w-full mt-2 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                                    {searchingClients ? (
                                                        <div className="p-4 text-center text-sm text-base-content/60">
                                                            Buscando...
                                                        </div>
                                                    ) : (
                                                        clients.map(client => (
                                                            <button
                                                                key={client.id}
                                                                className="w-full text-left p-3 hover:bg-base-200 border-b border-base-300 last:border-b-0"
                                                                onClick={() => {
                                                                    setSelectedClient(
                                                                        client
                                                                    )
                                                                    setClientId(
                                                                        client.id
                                                                    )
                                                                    setClientSearch(
                                                                        ''
                                                                    )
                                                                    setShowClientSearch(
                                                                        false
                                                                    )
                                                                    if (isProfessionalMode) {
                                                                        setNotificationEmail(client.email || '')
                                                                        setWhatsappNumber(client.whatsAppNumber || '')
                                                                    }
                                                                }}
                                                            >
                                                                <div className="font-medium">
                                                                    {
                                                                        client.name
                                                                    }
                                                                </div>
                                                                {client.email && (
                                                                    <div className="text-xs text-base-content/60">
                                                                        {
                                                                            client.email
                                                                        }
                                                                    </div>
                                                                )}
                                                            </button>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                    </div>
                                )}
                            </div>
                        </FormGroup>

                        <FormGroup>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Descrição (opcional)"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                rows={2}
                            />
                        </FormGroup>

                        {!isProfessionalMode && !isTask && (
                            <div className="space-y-3">
                                <div className="form-control">
                                    <label className="label cursor-pointer justify-start gap-3">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-secondary"
                                            checked={isRecurring}
                                            onChange={e =>
                                                setIsRecurring(
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                        <span className="label-text">
                                            Evento recorrente
                                        </span>
                                    </label>
                                </div>
                                {isRecurring && (
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text text-sm">
                                                Frequência
                                            </span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={frequency}
                                            onChange={e =>
                                                setFrequency(
                                                    e.target
                                                        .value as RecurringFrequency,
                                                )
                                            }
                                        >
                                            {Object.entries(
                                                FREQUENCY_LABELS,
                                            ).map(([value, label]) => (
                                                <option
                                                    key={value}
                                                    value={value}
                                                >
                                                    {label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Agenda médica já tem avisos próprios (lembretes do atendimento),
                            então esses campos de notificação só aparecem fora dela. */}
                        {!isProfessionalMode && !isTask && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text text-sm">
                                            E-mail para notificações
                                        </span>
                                    </label>
                                    <input
                                        type="email"
                                        placeholder="E-mail para notificações"
                                        className="input input-bordered w-full"
                                        value={notificationEmail}
                                        onChange={e =>
                                            setNotificationEmail(e.target.value)
                                        }
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text text-sm">
                                            WhatsApp para notificações
                                        </span>
                                    </label>
                                    <input
                                        type="tel"
                                        placeholder="WhatsApp para notificações"
                                        className="input input-bordered w-full"
                                        value={whatsappNumber}
                                        onChange={e =>
                                            setWhatsappNumber(e.target.value)
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <ModalActions>
                        <button className="btn btn-ghost" onClick={onClose}>
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? 'Salvando...' : 'Adicionar'}
                        </button>
                    </ModalActions>
                </ModalContainer>
            </dialog>

            <BackdropLoading visible={loading} />
        </React.Fragment>
    )
}
